// src/lib/emitter-resolver.js — Architecture multi-émetteur (B3, 06/05/2026)
//
// ⚠️ SOURCE UNIQUE pour le serveur ET l'écran (F10 phase 2, 03/10). Écrit en
// CommonJS pur, zéro dépendance : l'écran (FactureTab, ContratTab,
// LogementsPanel, ResaV2Modal) l'importe directement ; le serveur le lit par
// le relais `lib/emitter-resolver.js` (api/sites.js). Ne rien ajouter qui ne
// soit chargeable des deux côtés (garde : src/lib/doublons-lib-conformite.test.js).
//
// `propDisplayName` (normalement src/lib/format.js) est INLINÉ ici (libellé proprio
// NOM PRÉNOM) : format.js est écrit en import/export, que le serveur ne sait
// pas charger. Même corps que format.js (l'ancienne copie écran l'importait).
//
// Règle (décision 34 du 30/10, lot A1 — la sous-location est une DONNÉE) :
//   - prop.sous_location === true  → émetteur la conciergerie (type 'fbz') :
//     les chambres qu'elle sous-loue, pièce à SON nom, série V ;
//   - prop.sous_location === false → émetteur le propriétaire (type 'proprio') ;
//   - donnée absente → repli historique : prop.id === 'p6' (Maison Zen). Le
//     repli disparaîtra quand la donnée sera posée sur la fiche (écriture en
//     base = oui de Dimitri, hors de ce lot).
// La conciergerie émettrice vient de cfg.co (l'entrée), jamais d'un littéral.

const SOUS_LOC_PROP_ID = 'p6';

function estSousLocation(prop) {
  if (prop && typeof prop.sous_location === 'boolean') return prop.sous_location;
  return prop?.id === SOUS_LOC_PROP_ID;
}

// Inliné depuis src/lib/format.js (propDisplayName). NOM (maj) + Prénom.
// Fallback prop.nom pour les proprios non encore migrés (ADR-050).
function propDisplayName(prop) {
  if (!prop) return '';
  const nf = String(prop.nom_famille || '').trim().toUpperCase();
  const pn = String(prop.prenom || '').trim();
  if (nf || pn) return [nf, pn].filter(Boolean).join(' ');
  return prop.nom || '';
}

/**
 * Numéro de TVA à imprimer sur un document légal de la conciergerie — SOURCE
 * UNIQUE (facture propriétaire, avoir, facture/reçu voyageur via l'émetteur).
 *
 * Décisions du 28/10 (n° 25 et 34) puis consigne Dimitri du 29/10 : plus
 * AUCUN numéro écrit en dur (le repli FR65927602805 a été imprimé à tort sur
 * 29 factures, figées). Un numéro s'imprime SEULEMENT si la conciergerie est
 * assujettie (`co.tva === true`) ET a saisi SON numéro (`co.numeroTva` non
 * vide après trim). Sinon '' → les gabarits impriment la mention
 * « TVA non applicable, art. 293 B du CGI ». Valeur BRUTE : chaque gabarit
 * l'échappe à l'impression.
 *
 * @param {object} co — cfg.co
 * @returns {string} le numéro saisi, ou ''
 */
function numeroTvaImprimable(co) {
  if (!co || co.tva !== true) return '';
  return String(co.numeroTva ?? '').trim();
}

/**
 * Calcule les coordonnées émetteur d'un document légal.
 * @param {object} args
 * @param {object} args.cfg  — fbz8-cfg (cfg.co.* pour société FBZ)
 * @param {object} args.prop — propriétaire (id, nom_famille, prenom, adresse…)
 * @returns {{type:'fbz'|'proprio', nom, adresse, code_postal, ville, siret, tva,
 *           email, telephone, isSousLoc}}
 */
function resolveEmitter({ cfg, prop }) {
  const isSousLoc = estSousLocation(prop);
  const co = cfg?.co || {};

  if (isSousLoc) {
    return {
      type: 'fbz',
      nom: co.nom || 'FULLBNBZEN SAS',
      adresse: co.adresse || '',
      code_postal: '',
      ville: '',
      nom_famille: '',
      prenom: '',
      siret: co.siret || '927 602 805 00016',
      // Numéro de TVA : seulement celui SAISI par la conciergerie, et seulement
      // si elle est assujettie (voir numeroTvaImprimable). Sinon vide : le
      // gabarit voyageur imprime la mention 293 B pour la sous-loc.
      tva: numeroTvaImprimable(co),
      email: co.email || '',
      telephone: co.tel || '',
      isSousLoc: true,
    };
  }

  return {
    type: 'proprio',
    nom: propDisplayName(prop) || prop?.nom || '',
    // Parties du nom exposées SÉPARÉMENT (additif) : le CONTRAT les affiche en
    // « Prénom NOM » (décision Dimitri 2026-08-04 — un document qui engage
    // juridiquement ne peut pas porter le prénom seul, ni l'ordre d'un listing
    // administratif). `nom` reste inchangé : il sert aux factures et à l'admin,
    // dont l'ordre « NOM Prénom » est volontaire pour le tri.
    nom_famille: String(prop?.nom_famille || '').trim(),
    prenom: String(prop?.prenom || '').trim(),
    adresse: prop?.adresse || '',
    code_postal: prop?.code_postal || '',
    ville: prop?.ville || '',
    siret: '',
    tva: '',
    email: prop?.email || '',
    telephone: prop?.telephone || '',
    isSousLoc: false,
  };
}

module.exports = { resolveEmitter, numeroTvaImprimable, estSousLocation };
